import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { DollarSign, ShoppingCart, TrendingUp, RefreshCw, Calendar, Wallet, Store } from 'lucide-react';

// datetime-local (LOCAL) -> string para o input (YYYY-MM-DDTHH:MM)
const toLocalInput = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
// Início do dia (00:00) e agora, em formato de input local
const inicioHojeLocal = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return toLocalInput(d); };
const agoraLocal = () => toLocalInput(new Date());
// Converte um valor datetime-local (horário local do usuário) para ISO UTC "YYYY-MM-DDTHH:MM:SS"
const toUtcParam = (local) => {
  if (!local) return '';
  const d = new Date(local);
  return isNaN(d.getTime()) ? local : d.toISOString().slice(0, 19);
};
// Converte um ISO UTC (ex.: abertura_at do caixa) para valor do input datetime-local (horário local)
const utcToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : toLocalInput(d);
};
const fmtCaixaData = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const RelatorioVendasPage = () => {
  const [de, setDe] = useState(inicioHojeLocal());
  const [ate, setAte] = useState(agoraLocal());
  // Limites EXATOS (UTC, com segundos) usados na consulta. Quando definidos
  // (ex.: ao escolher um caixa), têm prioridade sobre os inputs de minuto.
  const [exactDe, setExactDe] = useState('');
  const [exactAte, setExactAte] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [caixas, setCaixas] = useState([]);
  const [caixaId, setCaixaId] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const dep = exactDe || toUtcParam(de);
      const atep = exactAte || toUtcParam(ate);
      const r = await api.get(`/reports/vendas-resumo?de=${dep}&ate=${atep}`);
      setData(r.data);
    } catch (e) {
      toast.error('Erro ao carregar o relatório de vendas');
    } finally {
      setLoading(false);
    }
  }, [de, ate, exactDe, exactAte]);

  useEffect(() => { carregar(); }, [carregar]);

  // Carrega as sessões de caixa (para o seletor "usar período do caixa")
  useEffect(() => {
    api.get('/caixa/historico?limit=50')
      .then((r) => setCaixas(r.data.caixas || []))
      .catch(() => setCaixas([]));
  }, []);

  const setHoje = () => { setCaixaId(''); setExactDe(''); setExactAte(''); setDe(inicioHojeLocal()); setAte(agoraLocal()); };
  const setOntem = () => {
    setCaixaId(''); setExactDe(''); setExactAte('');
    const ini = new Date(); ini.setDate(ini.getDate() - 1); ini.setHours(0, 0, 0, 0);
    const fim = new Date(); fim.setDate(fim.getDate() - 1); fim.setHours(23, 59, 59, 0);
    setDe(toLocalInput(ini)); setAte(toLocalInput(fim));
  };

  // Ao escolher uma sessão de caixa: usa abertura/fechamento EXATOS (UTC) na
  // consulta e mostra o horário local (aproximado ao minuto) nos inputs.
  const selecionarCaixa = (id) => {
    setCaixaId(id);
    const c = caixas.find((x) => x.id === id);
    if (!c) return;
    setDe(utcToLocalInput(c.abertura_at));
    setExactDe(new Date(c.abertura_at).toISOString().slice(0, 19));
    if (c.fechamento_at) {
      setAte(utcToLocalInput(c.fechamento_at));
      setExactAte(new Date(c.fechamento_at).toISOString().slice(0, 19));
    } else {
      setAte(agoraLocal());
      setExactAte(new Date().toISOString().slice(0, 19));
    }
  };
  const onChangeDe = (v) => { setCaixaId(''); setExactDe(''); setDe(v); };
  const onChangeAte = (v) => { setCaixaId(''); setExactAte(''); setAte(v); };

  const formas = data?.por_forma_pagamento || [];
  const totalGeral = data?.total_geral || 0;

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="relatorio-vendas-page">
      <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Relatório de Vendas</h1>
          <p className="text-slate-600">Total vendido e total por forma de pagamento</p>
        </div>
        <Button variant="outline" onClick={carregar} data-testid="btn-refresh-relatorio">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Atualizar
        </Button>
      </div>

      {/* Filtro de período */}
      <Card className="mb-6">
        <CardContent className="pt-6 space-y-4">
          {/* Seletor "usar período do caixa" */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2 text-slate-500">
              <Store className="w-4 h-4" /> <Label className="text-slate-700">Usar período do caixa:</Label>
            </div>
            <Select value={caixaId || 'nenhum'} onValueChange={(v) => (v === 'nenhum' ? setCaixaId('') : selecionarCaixa(v))}>
              <SelectTrigger className="w-[420px] max-w-full" data-testid="select-caixa">
                <SelectValue placeholder="Escolha uma sessão de caixa..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nenhum">— Período manual —</SelectItem>
                {caixas.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {(c.status === 'aberto' ? '🟢 ABERTO' : '⚪ Fechado')} • {c.user_name || 'Operador'} • {fmtCaixaData(c.abertura_at)} → {c.fechamento_at ? fmtCaixaData(c.fechamento_at) : 'agora'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-400">Preenche a data/hora de abertura e fechamento automaticamente</span>
          </div>

          <div className="flex flex-wrap items-end gap-4 border-t pt-4">
            <div className="flex items-center gap-2 text-slate-500">
              <Calendar className="w-4 h-4" /> <Label className="text-slate-700">Período:</Label>
            </div>
            <div>
              <Label className="text-xs text-slate-500">De (data e hora)</Label>
              <Input type="datetime-local" value={de} max={ate} onChange={(e) => onChangeDe(e.target.value)} className="w-56" data-testid="input-de" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Até (data e hora)</Label>
              <Input type="datetime-local" value={ate} min={de} onChange={(e) => onChangeAte(e.target.value)} className="w-56" data-testid="input-ate" />
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={setHoje} data-testid="btn-hoje">Hoje</Button>
              <Button variant="secondary" size="sm" onClick={setOntem} data-testid="btn-ontem">Ontem</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card data-testid="card-total-geral" className="border-emerald-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-500" /> Total vendido (geral)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600" data-testid="valor-total-geral">{formatCurrency(totalGeral)}</div>
          </CardContent>
        </Card>
        <Card data-testid="card-qtd-vendas">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-blue-500" /> Nº de vendas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{data?.quantidade_vendas || 0}</div>
          </CardContent>
        </Card>
        <Card data-testid="card-ticket-medio">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-500" /> Ticket médio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-indigo-600">{formatCurrency(data?.ticket_medio || 0)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabela por forma de pagamento */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Wallet className="w-5 h-5 text-amber-500" /> Total por forma de pagamento
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="tabela-formas">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3 font-medium">Forma de pagamento</th>
                <th className="text-center p-3 font-medium">Nº lançamentos</th>
                <th className="text-right p-3 font-medium">% do total</th>
                <th className="text-right p-3 font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" className="p-8 text-center text-slate-500">Carregando...</td></tr>
              ) : formas.length === 0 ? (
                <tr><td colSpan="4" className="p-8 text-center text-slate-500">Nenhuma venda no período selecionado.</td></tr>
              ) : formas.map((f) => {
                const pct = totalGeral > 0 ? (f.total / totalGeral) * 100 : 0;
                return (
                  <tr key={f.code} className="border-b hover:bg-slate-50" data-testid={`row-forma-${f.code}`}>
                    <td className="p-3 font-medium">{f.label}</td>
                    <td className="p-3 text-center text-slate-600">{f.quantidade}</td>
                    <td className="p-3 text-right text-slate-500">{pct.toFixed(1)}%</td>
                    <td className="p-3 text-right font-semibold text-slate-800">{formatCurrency(f.total)}</td>
                  </tr>
                );
              })}
            </tbody>
            {!loading && formas.length > 0 && (
              <tfoot>
                <tr className="bg-slate-900 text-white">
                  <td className="p-3 font-bold" colSpan="3">TOTAL GERAL</td>
                  <td className="p-3 text-right font-bold" data-testid="rodape-total-geral">{formatCurrency(totalGeral)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default RelatorioVendasPage;
